import { getChartingResource, postChartingForm } from '@/features/charting-next/api/chartingClient';
import { normalizeChartingFavoriteFolders } from '@/features/charting-next/adapters/chartingPreferenceAdapters';
import { buildChartingFavoriteMembershipPayload } from '@/features/charting-next/adapters/chartingPayloads';
import { invalidateChartingWorkspaceMembership } from '@/features/charting-next/api/chartingCache';
import { forumId } from '../models/forumMappers';
import { forumTicker } from '../models/forumStock';
import { ForumError, staleResponse } from './forumErrors';

const invalid = () => new ForumError('workspace_invalid', 'No se pudo comprobar la pertenencia a tus workspaces. Vuelve a consultar su estado.');
const failure = error => {
  if (error instanceof ForumError) return error;
  const status = error?.status || error?.response?.status || null;
  return new ForumError('workspace_unavailable', [401, 403].includes(status) ? 'No tienes acceso a estos workspaces.' : 'No se pudieron consultar tus workspaces. Inténtalo de nuevo.', status);
};

export function createForumStockWorkspaceRepository({ getIdentity, canUse, get = getChartingResource, post = postChartingForm, invalidate = invalidateChartingWorkspaceMembership }) {
  let snapshot = null, flight = null;
  const owner = () => {
    const identity = getIdentity();
    if (!forumId(identity?.uid) || !canUse()) throw new ForumError('workspace_denied', 'Tu acceso actual no permite guardar valores en workspaces.', 403);
    return { uid: String(identity.uid), key: JSON.stringify(identity) };
  };
  const current = expected => { if (owner().key !== expected.key) throw staleResponse(); };
  const evict = uid => { try { Promise.resolve(invalidate(uid)).catch(() => {}); } catch (_) { /* Never repeat a write because cache eviction failed. */ } };
  async function load(ticker) {
    const expected = owner(), symbol = forumTicker(ticker); snapshot = null;
    if (!symbol) throw invalid();
    try {
      const payload = await get('/api/v1/stock-finder/favorite-folder/list', { params: { ticker: symbol } });
      current(expected);
      if (!payload || String(payload.user_id) !== expected.uid) throw staleResponse();
      if (payload.ticker !== symbol || !Array.isArray(payload.folders) || !Array.isArray(payload.selected_folders)) throw invalid();
      const folders = normalizeChartingFavoriteFolders(payload), names = folders.map(folder => folder.name);
      if (folders.length !== payload.folders.length || new Set(names).size !== names.length
        || payload.selected_folders.some(name => typeof name !== 'string' || !names.includes(name))) throw invalid();
      const result = { ticker: symbol, folders, selected: [...new Set(payload.selected_folders)] };
      snapshot = { owner: expected.key, result }; return result;
    } catch (error) { current(expected); throw failure(error); }
  }
  function change({ ticker, direction, folderName, value }) {
    let expected;
    try { expected = owner(); } catch (error) { return Promise.reject(error); }
    const symbol = forumTicker(ticker), key = JSON.stringify([expected.key, symbol, folderName, value]);
    if (flight) return flight.key === key ? flight.promise : Promise.reject(new ForumError('workspace_busy', 'Espera a que termine el cambio.', 409));
    if (!snapshot || snapshot.owner !== expected.key || snapshot.result.ticker !== symbol || typeof value !== 'boolean' || !snapshot.result.folders.some(folder => folder.name === folderName)) return Promise.reject(invalid());
    if (snapshot.result.selected.includes(folderName) === value) return Promise.resolve(snapshot.result);
    let submitted = false;
    const promise = Promise.resolve().then(async () => {
      current(expected);
      const payload = buildChartingFavoriteMembershipPayload({ ticker: symbol, direction: [0, 1, '0', '1'].includes(direction) ? Number(direction) : 0, folderName });
      submitted = true;
      const result = await post('/api/v1/stock-finder/' + (value ? 'save' : 'delete') + '/favorites', payload);
      current(expected);
      if (result !== true) throw invalid();
      const confirmed = await load(symbol); current(expected);
      if (confirmed.selected.includes(folderName) !== value) throw invalid();
      return confirmed;
    }).catch(error => {
      current(expected); snapshot = null;
      const problem = failure(error);
      if (problem.code === 'stale_response') throw problem;
      throw new ForumError('workspace_uncertain', 'No se pudo confirmar el cambio. Comprueba el estado antes de volver a modificarlo.', problem.status);
    }).finally(() => { if (submitted) evict(expected.uid); if (flight?.promise === promise) flight = null; });
    flight = { key, promise }; return promise;
  }
  return { load, change };
}
